import { useCallback, useRef } from 'react';
import {
  IKeyboardShortcutElementRefs,
  KeyboardShortcutCallback,
  TAvailableKeyboardShortcutChars,
} from '../interfaces/IKeyboardShortcutElementRefs';

const useKeyboardShortcuts = <T extends TAvailableKeyboardShortcutChars>(
  allowedKeys: readonly T[],
): IKeyboardShortcutElementRefs<T> => {
  // Use a ref to hold the mapping of allowed keys to sets of callback functions
  const shortcutsRef = useRef(new Map<T, Set<KeyboardShortcutCallback>>());

  const subscribe = useCallback((key: T, callback: KeyboardShortcutCallback): void => {
    if (!shortcutsRef.current.has(key)) {
      shortcutsRef.current.set(key, new Set());
    }
    shortcutsRef.current.get(key)?.add(callback);
  }, []);

  // Unsubscribe from a shortcut key by removing the callback from the set
  const unsubscribe = useCallback((key: T, callback: KeyboardShortcutCallback): void => {
    shortcutsRef.current.get(key)?.delete(callback);
  }, []);

  // Trigger all callbacks registered for the provided key
  const trigger = useCallback((key: T): void => {
    const callbacks = shortcutsRef.current.get(key);
    if (callbacks) {
      callbacks.forEach((cb) => cb());
    }
  }, []);

  const isValidKey = useCallback(
    (key: string): key is T => {
      return allowedKeys.includes(key as T);
    },
    [allowedKeys],
  );

  return { subscribe, unsubscribe, trigger, isValidKey };
};

export default useKeyboardShortcuts;
